// src/cockpit4/components/TokenUsagePanel.tsx
// ============================================
// Token 消耗记录面板（用户需求 #6）
//
// 显示：
//   1. 当日总览：总调用次数 / 总 token 消耗 / 总输入 / 总输出
//   2. 按用户聚合：每用户的调用次数、token 消耗、成功率
//   3. 最近调用明细：时间、用户、Provider、模型、token 数、耗时、状态
//   4. 历史日期查询：从 Vault 读取指定日期的完整日志
//
// 权限：仅超级管理员可查看（普通员工不开放）
// 数据源：tokenUsageTracker（内存缓存 + Vault XEAOS/Audit/TokenLog-{date}.md）
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Cpu,
  Clock,
  TrendingUp,
  TrendingDown,
  User,
  Zap,
  RefreshCw,
  Loader2,
  ChevronDown,
  ChevronRight,
  Lock,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { useUserStore } from "../../stores/userStore";
import { tokenUsageTracker, type TokenStats, type TokenUsageRecord } from "../../services/tokenUsageTracker";

// ============================================
// 工具函数
// ============================================

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso.slice(11, 19) || "-";
  }
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("zh-CN", { month: "2-digit", day: "2-digit" });
  } catch {
    return iso.slice(0, 10);
  }
}

// ============================================
// 主组件
// ============================================

export default function TokenUsagePanel() {
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  const [stats, setStats] = useState<TokenStats | null>(null);
  const [records, setRecords] = useState<TokenUsageRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | undefined>(undefined);
  const [showAllRecords, setShowAllRecords] = useState(false);

  // 历史日期查询
  const [queryDate, setQueryDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [historyRecords, setHistoryRecords] = useState<TokenUsageRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string>("");

  /** 刷新当日数据 */
  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // 强制刷新待写入记录到 Vault
      await tokenUsageTracker.flush();
      const s = tokenUsageTracker.getTodayStats();
      setStats(s);
      const r = tokenUsageTracker.getTodayRecords(showAllRecords ? 200 : 50, selectedUser);
      setRecords(r);
    } catch (err) {
      console.warn("[TokenUsagePanel] 刷新失败", err);
    } finally {
      setLoading(false);
    }
  }, [selectedUser, showAllRecords]);

  /** 查询历史日期日志 */
  const queryHistory = useCallback(async (date: string) => {
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const result = await tokenUsageTracker.queryDailyLog(date);
      if (result.success) {
        setHistoryRecords(result.records);
      } else {
        setHistoryRecords([]);
        setHistoryError(`未找到 ${date} 的 Token 日志`);
      }
    } catch (err: any) {
      setHistoryError(err?.message || "查询失败");
      setHistoryRecords([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    // 每 30 秒自动刷新一次（获取最新的 token 使用数据）
    const interval = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(interval);
  }, [refresh]);

  // ============================================
  // 权限检查：仅超级管理员可查看
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="rounded-lg border border-red-500/20 bg-red-500/[0.02] p-4">
        <div className="flex flex-col items-center justify-center py-6">
          <Lock size={24} className="text-red-400 mb-2" />
          <p className="text-[12px] font-mono text-red-300 mb-1">权限受限</p>
          <p className="text-[10px] font-mono text-gray-500 text-center">
            Token 消耗记录仅对超级管理员开放。
            <br />
            普通员工无权查看。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染
  // ============================================

  return (
    <div className="space-y-3">
      {/* 顶部操作栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-gray-500">
            数据源：内存缓存 + Vault XEAOS/Audit/TokenLog-{new Date().toISOString().slice(0, 10)}.md
          </span>
        </div>
        <button
          onClick={() => void refresh()}
          disabled={loading}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition disabled:opacity-50"
        >
          <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
          {loading ? "加载中" : "刷新"}
        </button>
      </div>

      {/* 总览卡片 */}
      <div className="grid grid-cols-4 gap-2">
        {[
          { label: "总调用次数", value: stats?.totalCalls || 0, icon: Activity, color: "#06B6D4", fmt: (v: number) => formatNumber(v) },
          { label: "总Token消耗", value: stats?.totalTokens || 0, icon: Zap, color: "#F59E0B", fmt: (v: number) => formatNumber(v) },
          { label: "输入Token", value: stats?.totalPromptTokens || 0, icon: TrendingDown, color: "#8B5CF6", fmt: (v: number) => formatNumber(v) },
          { label: "输出Token", value: stats?.totalCompletionTokens || 0, icon: TrendingUp, color: "#10B981", fmt: (v: number) => formatNumber(v) },
        ].map((card, idx) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              className="relative rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 overflow-hidden"
            >
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${card.color}, transparent)` }} />
              <div className="flex items-center gap-1.5 mb-1.5">
                <Icon size={11} style={{ color: card.color }} />
                <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{card.label}</span>
              </div>
              <div className="text-[20px] font-mono font-bold" style={{ color: card.color }}>
                {card.fmt(card.value)}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* 按用户聚合 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/[0.06] flex items-center gap-2">
          <User size={11} className="text-cyan-400" />
          <span className="text-[11px] font-mono text-cyan-300 uppercase tracking-wider">按用户聚合</span>
          <span className="text-[9px] font-mono text-gray-600">
            ({stats?.byUser.length || 0} 个用户)
          </span>
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {(stats?.byUser || []).length === 0 ? (
            <div className="py-6 text-center text-[10px] font-mono text-gray-600">
              {loading ? <Loader2 size={14} className="mx-auto animate-spin" /> : "暂无 Token 使用记录"}
            </div>
          ) : (
            <table className="w-full text-[10px] font-mono">
              <thead className="sticky top-0 bg-[#0B1220]/95 backdrop-blur">
                <tr className="text-gray-500 border-b border-white/[0.04]">
                  <th className="px-3 py-1.5 text-left font-normal">用户</th>
                  <th className="px-3 py-1.5 text-left font-normal">角色</th>
                  <th className="px-3 py-1.5 text-right font-normal">调用次数</th>
                  <th className="px-3 py-1.5 text-right font-normal">成功</th>
                  <th className="px-3 py-1.5 text-right font-normal">失败</th>
                  <th className="px-3 py-1.5 text-right font-normal">输入Token</th>
                  <th className="px-3 py-1.5 text-right font-normal">输出Token</th>
                  <th className="px-3 py-1.5 text-right font-normal">总Token</th>
                  <th className="px-3 py-1.5 text-right font-normal">最后调用</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.byUser || []).map((u) => {
                  const isSelected = selectedUser === u.username;
                  return (
                      <tr
                        key={u.username}
                        className={`border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors ${isSelected ? "bg-cyan-500/[0.05]" : ""}`}
                        onClick={() => {
                          const newSelected = isSelected ? undefined : u.username;
                          setSelectedUser(newSelected);
                          const r = tokenUsageTracker.getTodayRecords(showAllRecords ? 200 : 50, newSelected);
                          setRecords(r);
                        }}
                      >
                        <td className="px-3 py-1.5 text-white">
                          <div className="flex items-center gap-1">
                            {isSelected ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
                            {u.username}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1 py-0.5 rounded text-[8px] ${u.role === "super_admin" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"}`}>
                            {u.role === "super_admin" ? "超管" : "员工"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-300">{u.callCount}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-400">{u.successCount}</td>
                        <td className="px-3 py-1.5 text-right text-red-400">{u.failedCount}</td>
                        <td className="px-3 py-1.5 text-right text-purple-300">{formatNumber(u.totalPromptTokens)}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-300">{formatNumber(u.totalCompletionTokens)}</td>
                        <td className="px-3 py-1.5 text-right text-amber-300 font-bold">{formatNumber(u.totalTokens)}</td>
                        <td className="px-3 py-1.5 text-right text-gray-500">{formatTime(u.lastCallAt)}</td>
                      </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 最近调用明细 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={11} className="text-purple-400" />
            <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider">最近调用明细</span>
            {selectedUser && (
              <span className="text-[9px] font-mono text-cyan-400">
                (筛选: {selectedUser})
                <button
                  onClick={() => {
                    setSelectedUser(undefined);
                    setRecords(tokenUsageTracker.getTodayRecords(showAllRecords ? 200 : 50));
                  }}
                  className="ml-1 text-gray-500 hover:text-white"
                >
                  ✕ 清除
                </button>
              </span>
            )}
          </div>
          <button
            onClick={() => {
              const newVal = !showAllRecords;
              setShowAllRecords(newVal);
              setRecords(tokenUsageTracker.getTodayRecords(newVal ? 200 : 50, selectedUser));
            }}
            className="text-[9px] font-mono text-gray-500 hover:text-white transition-colors"
          >
            {showAllRecords ? "显示最近50条" : "显示最近200条"}
          </button>
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {records.length === 0 ? (
            <div className="py-6 text-center text-[10px] font-mono text-gray-600">
              {loading ? <Loader2 size={14} className="mx-auto animate-spin" /> : "暂无调用记录"}
            </div>
          ) : (
            <table className="w-full text-[9px] font-mono">
              <thead className="sticky top-0 bg-[#0B1220]/95 backdrop-blur">
                <tr className="text-gray-500 border-b border-white/[0.04]">
                  <th className="px-2 py-1.5 text-left font-normal">时间</th>
                  <th className="px-2 py-1.5 text-left font-normal">用户</th>
                  <th className="px-2 py-1.5 text-left font-normal">Provider</th>
                  <th className="px-2 py-1.5 text-left font-normal">任务</th>
                  <th className="px-2 py-1.5 text-left font-normal">模型</th>
                  <th className="px-2 py-1.5 text-right font-normal">输入</th>
                  <th className="px-2 py-1.5 text-right font-normal">输出</th>
                  <th className="px-2 py-1.5 text-right font-normal">总Token</th>
                  <th className="px-2 py-1.5 text-right font-normal">耗时</th>
                  <th className="px-2 py-1.5 text-center font-normal">状态</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r, idx) => (
                  <tr key={idx} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="px-2 py-1 text-gray-400">{formatTime(r.timestamp)}</td>
                    <td className="px-2 py-1 text-gray-300">{r.username}</td>
                    <td className="px-2 py-1 text-cyan-400">{r.provider}</td>
                    <td className="px-2 py-1 text-gray-400">{r.task}</td>
                    <td className="px-2 py-1 text-purple-300" title={r.model}>
                      {r.model.length > 20 ? r.model.slice(0, 20) + "…" : r.model}
                    </td>
                    <td className="px-2 py-1 text-right text-purple-300">{r.promptTokens}</td>
                    <td className="px-2 py-1 text-right text-emerald-300">{r.completionTokens}</td>
                    <td className="px-2 py-1 text-right text-amber-300 font-bold">{r.totalTokens}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{r.latency}ms</td>
                    <td className="px-2 py-1 text-center">
                      {r.success ? (
                        <span className="text-emerald-400" title={r.error}>✓</span>
                      ) : (
                        <span className="text-red-400" title={r.error}>✕</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 历史日期查询 */}
      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] overflow-hidden">
        <div className="px-3 py-2 border-b border-white/[0.06] flex items-center gap-2">
          <Calendar size={11} className="text-amber-400" />
          <span className="text-[11px] font-mono text-amber-300 uppercase tracking-wider">历史日期查询</span>
          <input
            type="date"
            value={queryDate}
            onChange={(e) => setQueryDate(e.target.value)}
            className="ml-2 px-2 py-0.5 text-[10px] font-mono bg-white/[0.05] border border-white/[0.08] rounded text-white focus:outline-none focus:border-amber-500/30"
          />
          <button
            onClick={() => void queryHistory(queryDate)}
            disabled={historyLoading}
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 rounded transition-colors disabled:opacity-50"
          >
            {historyLoading ? <Loader2 size={10} className="animate-spin" /> : <Cpu size={10} />}
            查询
          </button>
        </div>

        {historyError && (
          <div className="px-3 py-2 text-[10px] font-mono text-amber-400 flex items-center gap-1">
            <AlertCircle size={10} />
            {historyError}
          </div>
        )}

        {historyRecords.length > 0 && (
          <div className="max-h-[320px] overflow-y-auto">
            <div className="px-3 py-1.5 text-[9px] font-mono text-gray-500 border-b border-white/[0.04]">
              {queryDate} 共 {historyRecords.length} 条记录 · 总 Token: {formatNumber(historyRecords.reduce((s, r) => s + r.totalTokens, 0))}
            </div>
            <table className="w-full text-[9px] font-mono">
              <thead className="sticky top-0 bg-[#0B1220]/95 backdrop-blur">
                <tr className="text-gray-500 border-b border-white/[0.04]">
                  <th className="px-2 py-1.5 text-left font-normal">时间</th>
                  <th className="px-2 py-1.5 text-left font-normal">用户</th>
                  <th className="px-2 py-1.5 text-left font-normal">角色</th>
                  <th className="px-2 py-1.5 text-left font-normal">Provider</th>
                  <th className="px-2 py-1.5 text-left font-normal">模型</th>
                  <th className="px-2 py-1.5 text-right font-normal">输入</th>
                  <th className="px-2 py-1.5 text-right font-normal">输出</th>
                  <th className="px-2 py-1.5 text-right font-normal">总Token</th>
                  <th className="px-2 py-1.5 text-right font-normal">耗时</th>
                  <th className="px-2 py-1.5 text-center font-normal">状态</th>
                </tr>
              </thead>
              <tbody>
                {historyRecords.slice(-200).reverse().map((r, idx) => (
                  <tr key={idx} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="px-2 py-1 text-gray-400">{formatTime(r.timestamp)}</td>
                    <td className="px-2 py-1 text-gray-300">{r.username}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1 py-0.5 rounded text-[8px] ${r.role === "super_admin" ? "bg-red-500/15 text-red-400" : "bg-blue-500/15 text-blue-400"}`}>
                        {r.role === "super_admin" ? "超管" : "员工"}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-cyan-400">{r.provider}</td>
                    <td className="px-2 py-1 text-purple-300" title={r.model}>
                      {r.model.length > 20 ? r.model.slice(0, 20) + "…" : r.model}
                    </td>
                    <td className="px-2 py-1 text-right text-purple-300">{r.promptTokens}</td>
                    <td className="px-2 py-1 text-right text-emerald-300">{r.completionTokens}</td>
                    <td className="px-2 py-1 text-right text-amber-300 font-bold">{r.totalTokens}</td>
                    <td className="px-2 py-1 text-right text-gray-500">{r.latency}ms</td>
                    <td className="px-2 py-1 text-center">
                      {r.success ? <span className="text-emerald-400">✓</span> : <span className="text-red-400" title={r.error}>✕</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
